import { deliveryFeeDisplay } from 'src/utils/deliveryFeeDisplay'
import { Checkbox, Tag } from 'antd'
import React from 'react'
import { formatDeliveryDays } from 'src/utils/deliveryDays'
import { CHECKOUT_LINE_GRID_COLUMNS } from 'src/utils/checkoutLineGrid'
import styled from 'styled-components/macro'
import CheckoutItemRow from './CheckoutItemRow'
import {
  RESTRICTED_SUPPLIER_BADGE,
  RESTRICTED_SUPPLIER_EXPLAINER,
} from 'src/utils/restrictedCopy'

const SupplierGroup = ({
  supplier,
  isSelected,
  onToggleSelection,
  onQuantityChange,
  onRemoveItem,
  duplicates,
  isPricesHidden,
  loadingOrderIds,
  errorOrderIds,
  onRetry,
  isRefetching,
}) => {
  const isItemDuplicate = item => {
    return duplicates.some(
      dup =>
        dup.productId === item.productId &&
        dup.supplierId === supplier.supplierId,
    )
  }

  const amounts = deliveryFeeDisplay(supplier)

  return (
    <SupplierContainer>
      <SupplierHeader>
        <SupplierInfo>
          <Checkbox
            checked={isSelected}
            onChange={e =>
              onToggleSelection(supplier.supplierId, e.target.checked)
            }
          >
            <SupplierTitle>
              <span>{supplier.supplierTitle}</span>
              {supplier.restricted && (
                <Tag color='orange' style={{ marginLeft: 8 }}>
                  {RESTRICTED_SUPPLIER_BADGE}
                </Tag>
              )}
            </SupplierTitle>
          </Checkbox>
          {supplier.restricted && (
            <>
              <RestrictedCopy>{RESTRICTED_SUPPLIER_EXPLAINER}</RestrictedCopy>
            </>
          )}
          {supplier.deliveryDays && supplier.deliveryDays.length > 0 && (
            <DeliveryDays>
              <strong>Delivery Days:</strong>{' '}
              {formatDeliveryDays(supplier.deliveryDays).join(', ')}
            </DeliveryDays>
          )}
        </SupplierInfo>
        <SupplierTotals>
          {isRefetching ? (
            <span>Updating totals…</span>
          ) : (
            <div style={{ textAlign: 'right', maxWidth: 320 }}>
              {!isPricesHidden && (
                <>
                  <div>Goods (ex VAT): £{amounts.goods.toFixed(2)}</div>
                  <div>
                    <strong>
                      Delivery (ex VAT): £{amounts.delivery.toFixed(2)}
                    </strong>
                  </div>
                  <Subtotal>
                    Order total (ex VAT): £{amounts.total.toFixed(2)}
                  </Subtotal>
                </>
              )}
              {amounts.message && (
                <div
                  role={!amounts.canOrder ? 'alert' : undefined}
                  style={{
                    color: amounts.canOrder ? '#666' : '#b42318',
                    marginTop: 8,
                  }}
                >
                  {amounts.message}
                </div>
              )}
            </div>
          )}
        </SupplierTotals>
      </SupplierHeader>

      <ItemsTable>
        <TableHeader>
          <HeaderCell />
          <HeaderCell>Title</HeaderCell>
          <HeaderCell>Size</HeaderCell>
          <HeaderCell>Price</HeaderCell>
          <HeaderCell>Supplier</HeaderCell>
          <HeaderCell>SKU</HeaderCell>
          <HeaderCell>Quantity</HeaderCell>
          <HeaderCell>Total</HeaderCell>
          <HeaderCell>Action</HeaderCell>
        </TableHeader>
        <ItemsList>
          {supplier.items.map(item => (
            <CheckoutItemRow
              key={item.orderId}
              item={{
                ...item,
                supplierTitle: supplier.supplierTitle,
              }}
              isDuplicate={isItemDuplicate(item)}
              onQuantityChange={onQuantityChange}
              onRemove={onRemoveItem}
              isPricesHidden={isPricesHidden}
              isLoading={loadingOrderIds?.has(item.orderId)}
              errorMessage={errorOrderIds?.get(item.orderId)}
              onRetry={onRetry}
            />
          ))}
        </ItemsList>
      </ItemsTable>
    </SupplierContainer>
  )
}

const SupplierContainer = styled.div`
  margin-top: 32px;
  margin-bottom: 32px;
  background: white;
  border-radius: 4px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const SupplierHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e8e8e8;
`

const SupplierInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SupplierTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const DeliveryDays = styled.div`
  font-size: 14px;
  color: #666;
  margin-left: 24px; // Align with checkbox
`

const SupplierTotals = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
`

const RestrictedCopy = styled.div`
  margin-left: 24px;
  margin-top: 2px;
  font-size: 12px;
  color: #666;
`

const Subtotal = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
  opacity: ${props => (props.$isRefetching ? 0.5 : 1)};
  transition: opacity 0.2s ease;
`

const ItemsTable = styled.div`
  width: 100%;
  overflow-x: auto;
`

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: ${CHECKOUT_LINE_GRID_COLUMNS};
  gap: 15px;
  min-width: 52rem;
  padding: 10px 15px;
  background-color: #fafafa;
  border-bottom: 2px solid #e8e8e8;
  font-weight: 600;
  font-size: 14px;
  color: #666;
`

const HeaderCell = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;

  &:nth-child(6) {
    white-space: nowrap;
    overflow-wrap: normal;
  }
`

const ItemsList = styled.div`
  width: 100%;
`

export default SupplierGroup
